import { useStyletron } from '@thanx/uikit/theme'
import NavLink from 'components/Navbar/Bar/NavLink'
import SectionHeader from 'components/Navbar/Bar/SectionHeader'
import { SECTION_IDS } from 'constants/deliveryLocationSettings'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import useIsAdmin from 'hooks/useIsAdmin'
import { useOrderingAccess } from 'hooks/useOrderingAccess'
import { useRbacNavDisabledWithReason } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import { isPostOrderFeedbackHidden } from 'scenes/Cms/Settings/PostOrderFeedback/constants'
import { useIsMobile } from 'utilities/responsive'

const t = buildTranslate('nav.settings_menu')

const COLUMN_MIN_WIDTH = '240px'

type SettingsContentProps = {
  isLeftNav?: boolean
  onClick?: VoidFunction
}

/**
 * Renders the settings navigation menu with links to ordering, loyalty,
 * and other settings pages. Visibility of each link is gated by feature
 * flags and RBAC permissions.
 */
const SettingsContent = ({
  isLeftNav = false,
  onClick,
}: SettingsContentProps) => {
  const [css] = useStyletron()
  const isMobile = useIsMobile()
  const merchant = useCurrentMerchant()
  const isAdmin = useIsAdmin()

  // Feature flags
  const unavailableItemVisibilityEnabled = useFlag(
    'mx-unavailable-item-visibility',
    false
  )
  const timeBasedMenuVisibilityFlag = useFlag(
    'olo-time-based-menu-visibility-mx',
    false
  )
  const timeBasedMenuVisibilityEnabled =
    timeBasedMenuVisibilityFlag &&
    Boolean(merchant?.supports_time_based_menu_visibility)
  const deliveryOptimizationEnabled = useFlag('delivery-optimization-mx', true)
  const preferencesModalSettingEnabled = useFlag(
    'preferences-modal-setting-enabled',
    false
  )
  const orderingErrorMessagesEnabled = useFlag(
    'ordering-error-message-customization',
    false
  )
  const orderingManagementEnabled = useFlag('mx-4-ordering-management', false)
  const instoreEnrollmentEnabled = useFlag('instore-enrollment-settings', false)
  const customizableLegalDocsFlag = useFlag('mx-customizable-legal-docs', false)
  const promoCodeVisibilityEnabled = useFlag(
    'promo-codes-visibility-override',
    false
  )
  const rewardExpirationEnabled = useFlag(
    'mx-granted-reward-expiration-settings',
    false
  )
  // When Operations Center is on, the three settings that the consolidated
  // Delivery & Location Settings page absorbed (DATA-4953) deep-link into
  // that page's sections (DATA-4954) instead of their standalone pages.
  // Anchor hashes match DeliveryLocationSettings' SECTION_IDS — guarded by
  // the Settings nav test so they can't drift.
  const operationsCenterEnabled = useFlag('operations-center', false)

  // RBAC permission checks — disabled with tooltip
  const orderingMgmt = useRbacNavDisabledWithReason('OrderingManagement')
  const loyaltySetup = useRbacNavDisabledWithReason('LoyaltySetup')
  const cmsPermission = useRbacNavDisabledWithReason('CMS')
  const merchantSettings = useRbacNavDisabledWithReason('MerchantSettings')
  const redeemManager = useRbacNavDisabledWithReason('RedeemManager')

  // Ordering error messages compound check — `orderingEnabled` from the hook
  // already encodes `merchant.ordering_enabled` + RBAC + capability.
  const { orderingEnabled } = useOrderingAccess()
  const showOrderingErrorMessages =
    orderingErrorMessagesEnabled && orderingManagementEnabled && orderingEnabled

  // Terms and policies — admin-only (behind customizable legal docs flag)
  const showTermsAndPolicies = customizableLegalDocsFlag && isAdmin

  const secondaryColumn = isMobile ? 1 : 2
  const secondaryRow = isMobile ? 2 : 1

  const style = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gridTemplateRows: isMobile ? 'auto auto' : '1fr',
    gap: isMobile ? '0px' : '0px 16px',
    alignItems: 'stretch',
    justifyItems: 'start',
  }

  return (
    <div className={css(style)}>
      {/* Left column */}
      <div
        className={css({
          gridColumn: '1',
          gridRow: '1',
          minWidth: COLUMN_MIN_WIDTH,
        })}
      >
        {!isLeftNav && <SectionHeader text={t('sections.ordering')} />}
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('ordering_configuration')}
          link={
            operationsCenterEnabled
              ? `/delivery_location_settings#${SECTION_IDS.orderingConfiguration}`
              : '/settings/ordering_configuration'
          }
          disabled={orderingMgmt.disabled}
          disabledReason={orderingMgmt.disabledReason}
        />
        {unavailableItemVisibilityEnabled && (
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('unavailable_item_visibility')}
            link="/settings/unavailable_item_visibility"
            disabled={orderingMgmt.disabled}
            disabledReason={orderingMgmt.disabledReason}
          />
        )}
        {timeBasedMenuVisibilityEnabled && (
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('time_based_menu_visibility')}
            link="/settings/time_based_menu_visibility"
            disabled={orderingMgmt.disabled}
            disabledReason={orderingMgmt.disabledReason}
          />
        )}
        {/* Gate on the RBAC-aware ordering access, not a hard-coded `manage`,
            so this matches BOTH the links' own `disabled={orderingMgmt.disabled}`
            and the DeliveryLocationSettings scene's `canEditOrdering` gate
            (`!useRbacNavDisabled('OrderingManagement')`). With RBAC on, an
            `access`-but-not-`manage` user now both sees the link and can reach
            the section DLS renders for them; with RBAC off this stays the
            legacy `manage` threshold. */}
        {!orderingMgmt.disabled && (
          <>
            {deliveryOptimizationEnabled && (
              <NavLink
                isLeftNav={isLeftNav}
                onClick={onClick}
                text={t('delivery_default_logic')}
                link={
                  operationsCenterEnabled
                    ? `/delivery_location_settings#${SECTION_IDS.deliveryDefault}`
                    : '/settings/delivery_default_logic'
                }
                disabled={orderingMgmt.disabled}
                disabledReason={orderingMgmt.disabledReason}
              />
            )}
            {preferencesModalSettingEnabled && (
              <NavLink
                isLeftNav={isLeftNav}
                onClick={onClick}
                text={t('preferences_modal_setting')}
                link={
                  operationsCenterEnabled
                    ? `/delivery_location_settings#${SECTION_IDS.locationSelection}`
                    : '/settings/preferences_modal_setting'
                }
                disabled={orderingMgmt.disabled}
                disabledReason={orderingMgmt.disabledReason}
              />
            )}
          </>
        )}
        {showOrderingErrorMessages && (
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('ordering_error_messages')}
            link="/settings/ordering_error_messages"
            disabled={orderingMgmt.disabled}
            disabledReason={orderingMgmt.disabledReason}
          />
        )}
        {merchant?.ordering_enabled && (
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('checkout_message')}
            link="/settings/checkout_message"
            disabled={orderingMgmt.disabled}
            disabledReason={orderingMgmt.disabledReason}
          />
        )}
        {promoCodeVisibilityEnabled && merchant?.ordering_enabled && (
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('promo_code_visibility')}
            link="/settings/promo_code_visibility"
            disabled={orderingMgmt.disabled}
            disabledReason={orderingMgmt.disabledReason}
          />
        )}

        {!isLeftNav && <div className="mb-m" />}
        {!isLeftNav && <SectionHeader text={t('sections.other')} />}
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('analytics')}
          link="/settings/analytics"
          disabled={cmsPermission.disabled}
          disabledReason={cmsPermission.disabledReason}
        />
        {showTermsAndPolicies && (
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('terms_and_policies')}
            link="/settings/terms_and_policies"
          />
        )}
      </div>

      {/* Right column */}
      <div
        className={css({
          gridColumn: secondaryColumn,
          gridRow: secondaryRow,
          minWidth: COLUMN_MIN_WIDTH,
        })}
      >
        {!isLeftNav && (
          <SectionHeader text={t('sections.loyalty_and_in_store')} />
        )}
        {rewardExpirationEnabled && (
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('reward_settings')}
            link="/rewards#settings"
            // /rewards#settings is reachable only with BOTH the RedeemManager
            // route gate (the /rewards page) and the MerchantSettings tab gate,
            // so disable when either is missing — otherwise a MerchantSettings-
            // only user clicks through and gets bounced by the RedeemManager
            // route guard with no feedback.
            disabled={merchantSettings.disabled || redeemManager.disabled}
            disabledReason={
              merchantSettings.disabledReason || redeemManager.disabledReason
            }
          />
        )}
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('receipt_settings')}
          link="/settings/receipt_settings"
          disabled={loyaltySetup.disabled}
          disabledReason={loyaltySetup.disabledReason}
        />
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('scannable_codes')}
          link="/settings/scannable_codes"
          disabled={loyaltySetup.disabled}
          disabledReason={loyaltySetup.disabledReason}
        />
        {!isPostOrderFeedbackHidden(merchant?.ordering_provider) && (
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('post_order_feedback')}
            link="/settings/post_order_feedback"
            disabled={loyaltySetup.disabled}
            disabledReason={loyaltySetup.disabledReason}
          />
        )}
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('email_reply_routing')}
          link="/settings/email_reply_routing"
          disabled={loyaltySetup.disabled}
          disabledReason={loyaltySetup.disabledReason}
        />
        {instoreEnrollmentEnabled &&
          merchant?.has_direct_integration_provider && (
            <NavLink
              isLeftNav={isLeftNav}
              onClick={onClick}
              text={t('instore_enrollment')}
              link="/settings/instore_enrollment"
              disabled={loyaltySetup.disabled}
              disabledReason={loyaltySetup.disabledReason}
            />
          )}
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('instore_purchase_tracking')}
          link="/settings/instore_purchase_tracking"
          disabled={loyaltySetup.disabled}
          disabledReason={loyaltySetup.disabledReason}
        />
      </div>
    </div>
  )
}

export default SettingsContent
